import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Radio, 
  QrCode, 
  Clock, 
  Check, 
  X, 
  MoreHorizontal, 
  ArrowUp, 
  ArrowDown, 
  UserX, 
  Square,
  Mic,
  Users,
  Music,
  Play,
  Pause,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Star,
  DollarSign,
  Settings
} from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';

const KaraokeQuickTour = ({ onComplete, onSkip }) => {
  const { format } = useCurrency();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to Karaoke Mode! 🎤",
      description: "Let's take a quick tour of your karaoke dashboard and learn how to run amazing sessions.",
      icon: <Mic className="w-12 h-12 text-purple-400" />,
      content: (
        <div className="space-y-4">
          <p className="text-purple-300">
            You're about to learn how to:
          </p>
          <ul className="space-y-2 text-purple-300">
            <li className="flex items-center space-x-2">
              <Radio className="w-4 h-4 text-green-400" />
              <span>Go Live and start your session</span>
            </li>
            <li className="flex items-center space-x-2">
              <QrCode className="w-4 h-4 text-green-400" />
              <span>Share QR codes and URLs with your audience</span>
            </li>
            <li className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-green-400" />
              <span>Review and approve song requests</span>
            </li>
            <li className="flex items-center space-x-2">
              <MoreHorizontal className="w-4 h-4 text-green-400" />
              <span>Manage your queue with advanced options</span>
            </li>
            <li className="flex items-center space-x-2">
              <Radio className="w-4 h-4 text-green-400" />
              <span>Announce upcoming songs</span>
            </li>
            <li className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Mark songs as performed and go offline</span>
            </li>
            <li className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-green-400" />
              <span>Configure paid request options</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      title: "Step 1: Go Live 🎬",
      description: "Start your karaoke session and get ready to accept requests.",
      icon: <Radio className="w-12 h-12 text-red-400" />,
      content: (
        <div className="space-y-4">
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-purple-100 font-semibold">Session Control</h4>
                <p className="text-purple-300 text-sm">Start and manage your karaoke session</p>
              </div>
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Radio className="w-4 h-4 mr-2" />
                Go Live
              </Button>
            </div>
          </div>
          <p className="text-purple-300 text-sm">
            Click "Go Live" to start your session. This will activate your request page and TV display.
          </p>
        </div>
      )
    },
    {
      title: "Step 2: Configure Paid Requests ⭐",
      description: "Set up optional paid request features to enhance your karaoke experience.",
      icon: <Star className="w-12 h-12 text-yellow-400" />,
      content: (
        <div className="space-y-4">
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-yellow-200 font-semibold">Paid Request Settings</h4>
                <p className="text-yellow-300 text-sm">Optional feature to allow audience members to skip the queue</p>
              </div>
              <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <h5 className="text-purple-100 font-semibold">Enable Paid Requests</h5>
              </div>
              <p className="text-purple-300 text-sm mb-3">
                Toggle this feature on/off. When enabled, audience members can pay to skip the queue.
              </p>
              <div className="bg-purple-800/50 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <span className="text-purple-200 text-sm">Paid Requests</span>
                  <div className="w-12 h-6 bg-green-600 rounded-full relative">
                    <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <h5 className="text-purple-100 font-semibold">Skip Queue Price</h5>
              </div>
              <p className="text-purple-300 text-sm mb-3">
                Set the price for audience members to skip the queue.
              </p>
              <div className="bg-purple-800/50 rounded-lg p-2">
                <span className="text-purple-200 font-mono">{format(5)}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="w-4 h-4 text-blue-400" />
              <h5 className="text-purple-100 font-semibold">Request Limit</h5>
            </div>
            <p className="text-purple-300 text-sm mb-3">
              Set the maximum number of paid requests that can be active at any time.
            </p>
            <div className="bg-purple-800/50 rounded-lg p-2">
              <span className="text-purple-200 font-mono">3 paid requests maximum</span>
            </div>
          </div>
          
          <p className="text-purple-300 text-sm">
            <strong>How it works:</strong> When enabled, audience members will see a "Skip the Queue" option when submitting requests. 
            Paid requests automatically go to the top of your queue and are marked with a star icon.
          </p>
        </div>
      )
    },
    {
      title: "Step 3: Share Your Request Page 📱",
      description: "Share QR codes and URLs so your audience can request songs.",
      icon: <QrCode className="w-12 h-12 text-purple-400" />,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <h4 className="text-purple-100 font-semibold mb-2">QR Code</h4>
              <div className="bg-white p-4 rounded-lg inline-block">
                <div className="w-24 h-24 bg-gray-200 rounded flex items-center justify-center">
                  <QrCode className="w-12 h-12 text-gray-600" />
                </div>
              </div>
              <p className="text-purple-300 text-sm mt-2">Display this on your TV or venue</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
              <h4 className="text-purple-100 font-semibold mb-2">Public URL</h4>
              <div className="bg-purple-800/50 p-3 rounded text-sm text-purple-200 font-mono">
                yoursongrequest.com/karaoke/your-name
              </div>
              <p className="text-purple-300 text-sm mt-2">Share this link with your audience</p>
            </div>
          </div>
          <p className="text-purple-300 text-sm">
            Your audience will use these to request songs from their phones!
          </p>
        </div>
      )
    },
    {
      title: "Step 4: Review Pending Requests ⏳",
      description: "Approve or deny song requests from your audience.",
      icon: <Clock className="w-12 h-12 text-yellow-400" />,
      content: (
        <div className="space-y-4">
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-yellow-200 font-semibold">"Bohemian Rhapsody"</h4>
                <p className="text-yellow-300 text-sm">by Queen</p>
                <p className="text-yellow-400 text-xs">Requested by: John</p>
              </div>
              <div className="flex space-x-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700">
                  <X className="w-4 h-4 mr-1" />
                  Deny
                </Button>
              </div>
            </div>
          </div>
          <p className="text-purple-300 text-sm">
            Review each request and decide whether to approve it for your queue or deny it.
          </p>
        </div>
      )
    },
    {
      title: "Step 5: Manage Your Queue 🎵",
      description: "Use advanced options to organize your approved songs.",
      icon: <MoreHorizontal className="w-12 h-12 text-purple-400" />,
      content: (
        <div className="space-y-4">
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-purple-100 font-semibold">"Hotel California"</h4>
                <p className="text-purple-300 text-sm">by Eagles</p>
                <p className="text-purple-400 text-xs">Requested by: Sarah</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                  <Check className="w-4 h-4 mr-1" />
                  Done
                </Button>
                <Button size="sm" variant="outline" className="border-purple-600 text-purple-300">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="bg-purple-800/50 rounded-lg p-3 mt-2">
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2 text-purple-200 hover:bg-purple-700/50 p-1 rounded cursor-pointer">
                  <ArrowUp className="w-4 h-4" />
                  <span>Move Up</span>
                </div>
                <div className="flex items-center space-x-2 text-purple-200 hover:bg-purple-700/50 p-1 rounded cursor-pointer">
                  <ArrowDown className="w-4 h-4" />
                  <span>Move Down</span>
                </div>
                <div className="flex items-center space-x-2 text-purple-200 hover:bg-purple-700/50 p-1 rounded cursor-pointer">
                  <Radio className="w-4 h-4" />
                  <span>Announce Up Next</span>
                </div>
                <div className="flex items-center space-x-2 text-red-300 hover:bg-red-700/50 p-1 rounded cursor-pointer">
                  <UserX className="w-4 h-4" />
                  <span>Remove from Queue</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-purple-300 text-sm">
            Use the "..." menu to reorder songs, announce upcoming performers, or remove songs from the queue.
          </p>
        </div>
      )
    },
    {
      title: "Step 6: Announce Up Next! 📢",
      description: "Create excitement by announcing the next performer.",
      icon: <Radio className="w-12 h-12 text-blue-400" />,
      content: (
        <div className="space-y-4">
          <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4 text-center">
            <h4 className="text-blue-200 font-bold text-xl mb-2">UP NEXT</h4>
            <div className="text-blue-100 text-2xl font-bold mb-2">Hotel California</div>
            <div className="text-blue-300 text-lg mb-3">by Eagles</div>
            <div className="bg-blue-600/50 rounded-full px-4 py-2 inline-block">
              <span className="text-blue-100 text-sm">Requested by Sarah</span>
            </div>
            <div className="flex justify-center space-x-4 mt-4">
              <span className="text-2xl">🎵</span>
              <span className="text-2xl">🎶</span>
              <span className="text-2xl">🎤</span>
            </div>
          </div>
          <p className="text-purple-300 text-sm">
            Click "Announce Up Next" to display this exciting announcement on your TV display!
          </p>
        </div>
      )
    },
    {
      title: "Step 7: Mark as Done & Go Offline ✅",
      description: "Complete your session and end the karaoke night.",
      icon: <Square className="w-12 h-12 text-green-400" />,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
              <h4 className="text-green-200 font-semibold mb-2">Mark as Done</h4>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100">"Hotel California"</p>
                  <p className="text-green-300 text-sm">by Eagles</p>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                  <Check className="w-4 h-4 mr-1" />
                  Done
                </Button>
              </div>
            </div>
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
              <h4 className="text-red-200 font-semibold mb-2">End Session</h4>
              <p className="text-red-300 text-sm mb-3">When your karaoke night is complete</p>
              <Button size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700">
                <Square className="w-4 h-4 mr-1" />
                End Session
              </Button>
            </div>
          </div>
          <p className="text-purple-300 text-sm">
            Mark songs as "Done" when performed, and click "End Session" when your karaoke night is complete.
          </p>
        </div>
      )
    },
    {
      title: "You're Ready to Rock! 🎸",
      description: "You now know everything you need to run amazing karaoke sessions.",
      icon: <Mic className="w-12 h-12 text-purple-400" />,
      content: (
        <div className="space-y-4 text-center">
          <div className="bg-purple-900/30 border border-purple-600/50 rounded-lg p-6">
            <h4 className="text-purple-100 font-bold text-xl mb-4">🎤 Karaoke Master Checklist 🎤</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Go Live</span>
                </div>
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Configure Paid Requests</span>
                </div>
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Share QR/URL</span>
                </div>
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Review Requests</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Manage Queue</span>
                </div>
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Announce Up Next</span>
                </div>
                <div className="flex items-center space-x-2 text-green-300">
                  <Check className="w-4 h-4" />
                  <span>Go Offline</span>
                </div>
              </div>
            </div>
          </div>
          <p className="text-purple-300">
            You're all set! Head to your Live Dashboard to start your first karaoke session.
          </p>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-3 sm:p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="max-w-4xl w-full my-4 sm:my-8 max-h-[calc(100vh-2rem)] overflow-y-auto"
      >
        <Card className="bg-purple-900/90 border-purple-600 backdrop-blur-sm">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {currentStepData.icon}
            </div>
            <CardTitle className="text-2xl text-purple-100">
              {currentStepData.title}
            </CardTitle>
            <CardDescription className="text-purple-300 text-lg">
              {currentStepData.description}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {currentStepData.content}
              </motion.div>
            </AnimatePresence>

            {/* Progress Bar */}
            <div className="w-full bg-purple-800/50 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  onClick={onSkip}
                  variant="outline"
                  className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                >
                  Skip Tour
                </Button>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  variant="outline"
                  className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                <Button
                  onClick={nextStep}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {currentStep === steps.length - 1 ? (
                    <>
                      <Radio className="w-4 h-4 mr-2" />
                      Start Karaoke Session
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default KaraokeQuickTour;
